import React, { useState, useEffect, useRef } from "react";
import styles from "./Notices.module.css";
import api from "../../../api/axios";
import { 
  Building, Wrench, Shield, Car, Zap, Droplet, Users, AlertTriangle, BellRing, Sparkles, LayoutGrid, Bell, FileText, Clock, MapPin, Search
} from "lucide-react";

const noticeCategories = [
  { name: "All Notices", icon: <LayoutGrid size={16} /> },
  { name: "General", icon: <Building size={16} /> },
  { name: "Maintenance", icon: <Wrench size={16} /> },
  { name: "Security", icon: <Shield size={16} /> },
  { name: "Parking", icon: <Car size={16} /> },
  { name: "Electricity", icon: <Zap size={16} /> },
  { name: "Water", icon: <Droplet size={16} /> },
  { name: "Meeting", icon: <Users size={16} /> },
  { name: "Emergency", icon: <AlertTriangle size={16} /> },
  { name: "Rules", icon: <FileText size={16} /> },
];

const Notices = () => {
  const [noticesData, setNoticesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All Notices");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const { data } = await api.get('/public/notices');
        setNoticesData(data);
      } catch (error) {
        console.error("Failed to fetch notices", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotices();
  }, []);

  const filteredNotices = noticesData.filter((notice) => {
    const matchesCategory = activeCategory === "All Notices" || notice.category === activeCategory;
    const matchesSearch = notice.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (notice.description || notice.content || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className={styles.noticesPage}>
      {/* Header */}
      <div className={styles.headerSection}>
        <div className={styles.headerPattern}></div>
        <div className={styles.headerText}>
          <h1 className={styles.pageTitle}>Notice Board</h1>
          <p className={styles.leadText}>
            Stay updated with the latest society announcements and circulars.
          </p>
        </div>
        <div className={styles.headerDivider}>
          <span className={styles.dividerIcon}><Sparkles size={20} color="#0f766e" /></span>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Search Bar UI */}
        <div className={styles.searchContainerWrapper}>
          <div className={styles.searchBar}>
            
            {/* Category Dropdown */}
            <div className={styles.categoryDropdownContainer} ref={dropdownRef}>
              <button 
                className={styles.dropdownToggle}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className={styles.activeCategoryDisplay}>
                  {noticeCategories.find(c => c.name === activeCategory)?.icon || <LayoutGrid size={18} />}
                  <span>{activeCategory === "All Notices" ? "All Categories" : activeCategory}</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {noticeCategories.map((cat) => (
                    <div 
                      key={cat.name} 
                      className={`${styles.dropdownItem} ${activeCategory === cat.name ? styles.dropdownItemActive : ''}`}
                      onClick={() => {
                        setActiveCategory(cat.name);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className={styles.dropdownItemIcon}>{cat.icon || <Building size={16} />}</span>
                      <span>{cat.name === "All Notices" ? "All Categories" : cat.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className={styles.searchDivider}></div>

            {/* Search Input */}
            <div className={styles.searchInputWrapper}>
              <Search size={20} className={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Search notices, announcements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {/* Search Button */}
            <button className={styles.searchActionBtn}>
              <Search size={18} /> Search
            </button>
          </div>
        </div>

        {/* Notices List */}
        <div className={styles.noticeList}>
          {loading ? (
            <p>Loading notices...</p>
          ) : filteredNotices.map((notice) => {
            const dateObj = new Date(notice.createdAt || notice.date);
            const dateStr = dateObj.toLocaleString('default', { day: '2-digit', month: 'short', year: 'numeric' });
            return (
              <div key={notice._id} className={styles.noticeCard}>
                <div className={styles.noticeIconWrapper}>
                  <span className={styles.noticeIcon}><BellRing size={20} /></span>
                </div>

                <div className={styles.noticeContent}>
                  <div className={styles.noticeHeader}>
                    <span className={styles.categoryBadge}>
                      {notice.category || 'General'}
                    </span>
                    <span className={styles.publishDate}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        ></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {dateStr}
                    </span>
                  </div>

                  <h3 className={styles.noticeTitle}>{notice.title}</h3>
                  <p className={styles.noticeDescription}>{notice.description || notice.content}</p>

                  {(notice.meetingDateTime || notice.meetingLocation) && (
                    <div className={styles.meetingDetails}>
                      {notice.meetingDateTime && (
                        <span className={styles.meetingItem}>
                          <Clock size={15} /> {
                            !isNaN(new Date(notice.meetingDateTime).getTime())
                              ? new Date(notice.meetingDateTime).toLocaleString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                })
                              : notice.meetingDateTime
                          }
                        </span>
                      )}
                      {notice.meetingLocation && (
                        <span className={styles.meetingItem}>
                          <MapPin size={15} /> {notice.meetingLocation}
                        </span>
                      )}
                    </div>
                  )}

                  <div className={styles.noticeFooter}>
                    <span className={styles.authorInfo}>
                      <strong>Published by:</strong> {notice.author || 'Society Admin'}
                    </span>

                    {notice.attachment && (
                      <button className={styles.attachmentBtn}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                        </svg>
                        View Attachment
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {!loading && filteredNotices.length === 0 && (
            <div className={styles.noNotices} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '16px', minHeight: '200px' }}>
              <Bell size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', color: '#475569', marginBottom: '8px' }}>No Notices Found</h3>
              <p style={{ color: '#64748b', textAlign: 'center', margin: 0, fontSize: '14px' }}>There are no {activeCategory !== 'All Notices' ? activeCategory : ''} notices at the moment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notices;
